import React, { useCallback, useEffect, useState } from "react";
import { Card, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { urlEndPoint } from "helpers/GeneralHelper";
import {
  fetchRecentUserNotifications,
  markUserNotificationAsRead,
} from "model/UserNotificationModel";
import {
  extractNotificationListFromResponse,
  mapNotificationApiList,
} from "helpers/notificationListHelpers";
import SimpleNotificationListItem from "components/Common/SimpleNotificationListItem";

const SimpleNotificationModal = ({
  notificationModalOpenClose,
  setNotificationModalOpenClose,
  anchorRef,
}) => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const navigate = useNavigate();
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const loadRecent = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchRecentUserNotifications({});
      const { records } = extractNotificationListFromResponse(response);
      setNotifications(mapNotificationApiList(records));
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
     if (anchorRef?.current) {
       const rect = anchorRef.current.getBoundingClientRect();
       setPosition({
         top: rect.bottom + window.scrollY + 8,
         left: isMobile
           ? rect.right - 330 + window.scrollX
           : rect.right - 580 + window.scrollX,
       });
     }
   }, [notificationModalOpenClose, anchorRef, isMobile]);

  useEffect(() => {
    if (notificationModalOpenClose) {
      loadRecent();
    }
  }, [notificationModalOpenClose, loadRecent]);

  if (!notificationModalOpenClose) return null;

  const unreadCount = notifications.filter((item) => item.unread).length;

  const goToAllNotification = () => {
    navigate(`/${urlEndPoint.simpleAllNotifications}`);
    setNotificationModalOpenClose(false);
  };

  const handleNotificationClick = async (item = {}) => {
    const notificationId = String(item?.id ?? "").trim();
    if (!notificationId) return;

    if (item?.unread) {
      try {
        await markUserNotificationAsRead(notificationId);
        setNotifications((prev) =>
          prev.map((row) =>
            row.id === notificationId
              ? { ...row, read: true, unread: false, isRead: true }
              : row
          )
        );
      } catch {
        return;
      }
    }

    const link = String(item?.link ?? "").trim();
    if (link) {
      setNotificationModalOpenClose(false);
      if (/^https?:\/\//i.test(link)) {
        window.open(link, "_blank", "noopener,noreferrer");
      } else {
        navigate(link.startsWith("/") ? link : `/${link}`);
      }
    }
  };

  return (
    <>
      <div
        className="notification-backdrop"
        onClick={() => setNotificationModalOpenClose(false)}
      />

      <Card
        className="notification-dropdown simple-notification-dropdown"
        style={{
          top: position.top,
          left: position.left,
          width: isMobile ? "min(330px, calc(100vw - 24px))" : "min(520px, calc(100vw - 24px))",
        }}
      >
        <div className="notification-header">
          <h6 className="mb-1">Notifications</h6>
          <small>
            {loading
              ? "Loading…"
              : notifications.length
                ? unreadCount
                  ? `You have ${unreadCount} unread notification${
                      unreadCount === 1 ? "" : "s"
                    }`
                  : `You have ${notifications.length} notification${
                      notifications.length === 1 ? "" : "s"
                    }`
                : "No notifications yet"}
          </small>
        </div>

        <div className="notification-body simple-notification-feed">
          {loading ? (
            <div className="text-center py-4">
              <Spinner size="sm" color="secondary" />
            </div>
          ) : notifications.length ? (
            notifications.map((item) => (
              <SimpleNotificationListItem
                key={item.id}
                item={item}
                compact
                onClick={handleNotificationClick}
              />
            ))
          ) : (
            <div className="text-center text-muted py-4">No notifications</div>
          )}
        </div>

        <div className="notification-footer">
          <button
            type="button"
            className="btn btn-link w-100"
            onClick={goToAllNotification}
          >
            View All
          </button>
        </div>
      </Card>
    </>
  );
};

export default SimpleNotificationModal;
